// LocalizedGridCook3h.tsx
// добавлено свойство column.hidden
import * as React from 'react';
import {
  DataGrid,
  DataGridProps,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
  GridColumnResizeParams,
  GridColDef,
  GridToolbarProps,
} from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Button, Box } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import locales, { LocaleKey } from './locales';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';

interface CustomToolbarProps extends GridToolbarProps {
  onResetSettings?: () => void;
}

const CustomToolbar = (props: CustomToolbarProps) => {
  const { onResetSettings } = props;
  const { t } = useTranslation();
  return (
    <GridToolbarContainer sx={{ justifyContent: 'space-between', p: 1 }}>
      <Box>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <Button 
          startIcon={<RestartAltIcon />} 
          size="small" 
          onClick={onResetSettings}
        >
          {t('LocalizedGrid.Reset_settings')}
        </Button>
      </Box>
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );
};

// 1. Расширяем стандартный тип GridColDef, добавляя наше кастомное свойство hidden
export type CustomGridColDef = GridColDef & {
  hidden?: boolean;
};

// 2. В пропсах компонента заменяем GridColDef[] на наш CustomGridColDef[]
interface LocalizedGridProps extends Omit<DataGridProps, 'columns'> {
  lang: LocaleKey;
  gridId: string;
  columns: CustomGridColDef[]; // Используем расширенный тип
  hideToolbar?: boolean;
}

export default function LocalizedGrid({ 
  lang = 'uk', 
  gridId, 
  columns, 
  hideToolbar = false, 
  ...rest 
}: LocalizedGridProps) {

  const resolvedLang: LocaleKey = (lang && locales[lang]) ? lang : 'uk';
  const selectedLocale = locales[resolvedLang];

  const COOKIE_KEY = `dg_settings_${gridId}`;

  const [settings, setSettings] = React.useState(() => {
    const data = Cookies.get(COOKIE_KEY);
    return data ? JSON.parse(data) : {};
  });

  const persistChange = (key: string, value: any) => {
    setSettings((prev: any) => {
      const newSettings = { ...prev, [key]: value };
      Cookies.set(COOKIE_KEY, JSON.stringify(newSettings), { expires: 365 });
      return newSettings;
    });
  };

  const handleResetSettings = () => {
    Cookies.remove(COOKIE_KEY);
    window.location.reload();
  };

  // 3. Модифицируем processedColumns для перманентного удаления колонок с hidden: true
  const processedColumns = React.useMemo(() => {
    const savedWidths = settings.widths || {};
    
    return columns
      .filter((col) => !col.hidden) // <-- ПЕРМАНЕНТНО удаляем колонку из рендера и Toolbar меню
      .map((col) => {
        const savedWidth = savedWidths[col.field];
        if (savedWidth !== undefined) {
          return {
            ...col,
            width: savedWidth,
            flex: undefined, 
          };
        }
        return col;
      }) as GridColDef[]; // Приводим к базовому типу GridColDef для DataGrid
  }, [columns, settings.widths]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      localeText={selectedLocale.localizationProvider.components.MuiLocalizationProvider.defaultProps.localeText}
    >
      <Box sx={{ width: '100%', ...rest.sx }}>
        <DataGrid
          {...rest}
          columns={processedColumns}
          initialState={{
            ...rest.initialState,
            columns: {
              ...rest.initialState?.columns,
              columnVisibilityModel: {
                ...rest.initialState?.columns?.columnVisibilityModel,
                ...(settings.visibility || {}),
              },
              orderedFields: settings.order || [],
            },
          }}
          
          onColumnWidthChange={(params: GridColumnResizeParams) => {
            const currentWidths = settings.widths || {};
            const newWidths = { ...currentWidths, [params.colDef.field]: params.width };
            persistChange('widths', newWidths);
          }}
          
          onColumnVisibilityModelChange={(newModel) => {
            persistChange('visibility', newModel);
          }}

          onColumnOrderChange={(params: any) => {
            // ВАЖНО: берем поля из уже отфильтрованных processedColumns, чтобы индексы при перетаскивании не ломались
            const currentOrder = settings.order && settings.order.length > 0 
              ? [...settings.order] 
              : processedColumns.map(c => c.field);

            const field = params.column?.field || params.field;
            const targetIndex = params.targetIndex;

            if (field && targetIndex !== undefined) {
              const oldIndex = currentOrder.indexOf(field);
              if (oldIndex !== -1) {
                const newOrder = [...currentOrder];
                newOrder.splice(oldIndex, 1);
                newOrder.splice(targetIndex, 0, field);
                persistChange('order', newOrder);
              }
            }
          }}

          localeText={selectedLocale.dataGrid.components.MuiDataGrid.defaultProps.localeText}
          pageSizeOptions={[5, 10, 25, 50]}
          slots={{ 
            toolbar: hideToolbar ? null : CustomToolbar, 
          }}
          slotProps={{
            toolbar: {
              onResetSettings: handleResetSettings,
            } as CustomToolbarProps,
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}